import { createSelector } from '@ngrx/store';
import { Customer, CustomerSwiftCard } from '../client/src';
import { getCustomerFeatureState, ICustomerFeatureState } from '../reducers';
import {
  ICustomerDetails,
  ICustomerDetailsPage,
  LoadingDetails,
  LoadState,
} from '@nx-ng/types';
import {
  formatCustomerDetails,
  formatCustomerPageDetails,
} from '../util/format-customer-details';
import { customerHasMissingFields, readableErrors } from '../util';
import { ICustomerState } from '../reducers';
import { getCustomerLoadingDetails } from '../util/get-customer-loading-details';
import { IPreviousApplicantDetails } from '@nx-ng/types/previous-applicant-details';
import { ICustomerStateCustomer } from '../types';
import { getPreviousApplicantsDetails } from '../util/get-previous-applicants-details';
import { getLoadingDetails } from '@nx-ng/util/get-loading-details';

export const selectDataAccessCustomerDetailsState = createSelector(
  getCustomerFeatureState,
  (state: ICustomerFeatureState) => state?.customer
);

export const selectCustomerApplicantsState = createSelector(
  selectDataAccessCustomerDetailsState,
  (state: ICustomerState): ICustomerState['customerApplicants'] =>
    state?.customerApplicants
);

export const selectCustomerLoadedState = createSelector(
  selectDataAccessCustomerDetailsState,
  (state: ICustomerState) => state?.customer.loaded
);

export const selectCustomer = createSelector(
  selectDataAccessCustomerDetailsState,
  (state: ICustomerState): ICustomerStateCustomer => state?.customer
);

export const selectCustomerDetailsLoading = createSelector(
  selectCustomer,
  (customer: ICustomerStateCustomer | undefined): LoadingDetails =>
    getCustomerLoadingDetails(customer)
);

export const selectCustomerUpdatedState = createSelector(
  selectDataAccessCustomerDetailsState,
  (state: ICustomerState) => state?.customer.updated
);

export const selectCustomerError = createSelector(
  selectDataAccessCustomerDetailsState,
  (state: ICustomerState) =>
    readableErrors([state?.customer.loadError, state.customer.updateError])
);

export const selectCustomerDetails = createSelector(
  selectCustomer,
  (customer: ICustomerStateCustomer) => customer?.details
);

export const selectCustomerId = createSelector(
  selectCustomerDetails,
  (customer: Customer | undefined): string | undefined => customer?.id
);

export const selectFormattedCustomerDetails = createSelector(
  selectCustomer,
  (customer: ICustomerStateCustomer): ICustomerDetails | undefined => {
    return formatCustomerDetails(customer?.details);
  }
);

export const selectCustomerPageDetails = createSelector(
  selectCustomerDetails,
  (customer: Customer | undefined): ICustomerDetailsPage | undefined => {
    return formatCustomerPageDetails(customer);
  }
);

export const selectCustomerEmail = createSelector(
  selectCustomerDetails,
  (customer: Customer | undefined): string => customer?.emailAddress || ''
);

export const selectPreviousApplicants = createSelector(
  selectCustomerApplicantsState,
  (
    customerApplicantsState: ICustomerState['customerApplicants']
  ): IPreviousApplicantDetails[] =>
    getPreviousApplicantsDetails(customerApplicantsState?.applicants)
);

export const selectPreviousApplicantsLoading = createSelector(
  selectCustomerApplicantsState,
  (
    customerApplicantsState: ICustomerState['customerApplicants']
  ): LoadingDetails =>
    getLoadingDetails(customerApplicantsState?.loaded, 'loading_applicants')
);

export const selectPreviousApplicantsLoadedState = createSelector(
  selectCustomerApplicantsState,
  (customerApplicantsState: ICustomerState['customerApplicants']): LoadState =>
    customerApplicantsState.loaded
);

export const selectHasPreviousApplicants = createSelector(
  selectPreviousApplicants,
  (previousApplicants: IPreviousApplicantDetails[]): boolean =>
    previousApplicants.length > 0
);

export const selectCustomerHasMissingFields = createSelector(
  selectCustomerDetails,
  (customer: Customer | undefined): boolean =>
    customerHasMissingFields(customer)
);
